import React, { useState, useRef } from 'react';
import { stylistAPI } from '../../../../services/api';
import { X, Upload, Loader2 } from 'lucide-react';
import { ITEM_TYPES, SEASONS } from './constants';

const AddItemModal = ({ clientId, onClose, onAdded }) => {
  const [formData, setFormData] = useState({
    image_url: '',
    item_type: 'верх',
    season: 'круглогодичная',
    item_kind: '',
    notes: ''
  });
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const response = await stylistAPI.uploadImage(file);
      const fullUrl = response.url;
      setFormData({ ...formData, image_url: fullUrl });
    } catch (err) {
      console.error('Upload error:', err);
      alert('Ошибка загрузки изображения');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.image_url || !formData.item_kind) {
      alert('Заполните обязательные поля');
      return;
    }

    setLoading(true);
    try {
      await stylistAPI.addWardrobeItem(clientId, {
        ...formData,
        client_id: clientId
      });
      const items = await stylistAPI.getWardrobe(clientId);
      onAdded(items[0]);
    } catch (err) {
      console.error('Error adding item:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 'var(--space-l)', zIndex: 1000 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ backgroundColor: 'var(--white)', padding: 'var(--space-xxl)', maxWidth: '500px', width: '100%', maxHeight: '90vh', overflow: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-xl)' }}>
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.5rem' }}>Добавить вещь</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} /></button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Image Upload */}
          <div style={{ marginBottom: 'var(--space-l)' }}>
            <label style={{ display: 'block', fontFamily: 'var(--font-body)', fontSize: '0.75rem', letterSpacing: '1px', textTransform: 'uppercase', color: 'var(--mid-gray)', marginBottom: 'var(--space-s)' }}>
              Фото *
            </label>
            <input type="file" ref={fileInputRef} onChange={handleFileChange} accept="image/*" style={{ display: 'none' }} />
            
            {formData.image_url ? (
              <div style={{ position: 'relative', width: '150px', height: '150px' }}>
                <img src={formData.image_url} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, image_url: '' })}
                  style={{ position: 'absolute', top: '4px', right: '4px', padding: '4px', backgroundColor: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                >
                  <X size={14} />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                style={{
                  width: '150px',
                  height: '150px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 'var(--space-s)',
                  backgroundColor: 'var(--cream)',
                  border: '2px dashed var(--soft-gray)',
                  cursor: 'pointer'
                }}
              >
                {uploading ? <Loader2 size={24} style={{ animation: 'spin 1s linear infinite' }} /> : <Upload size={24} style={{ color: 'var(--mid-gray)' }} />}
                <span style={{ fontFamily: 'var(--font-body)', fontSize: '0.75rem', color: 'var(--mid-gray)' }}>
                  {uploading ? 'Загрузка...' : 'Выбрать фото'}
                </span>
              </button>
            )}
          </div>

          {/* Item Kind */}
          <div style={{ marginBottom: 'var(--space-l)' }}>
            <label style={{ display: 'block', fontFamily: 'var(--font-body)', fontSize: '0.75rem', letterSpacing: '1px', textTransform: 'uppercase', color: 'var(--mid-gray)', marginBottom: 'var(--space-s)' }}>
              Название *
            </label>
            <input
              type="text"
              value={formData.item_kind}
              onChange={(e) => setFormData({ ...formData, item_kind: e.target.value })}
              placeholder="Блузка, джинсы, платье..."
              style={{ width: '100%', padding: '12px', fontFamily: 'var(--font-body)', fontSize: '0.9375rem', border: '1px solid var(--soft-gray)', boxSizing: 'border-box' }}
            />
          </div>

          {/* Type */}
          <div style={{ marginBottom: 'var(--space-l)' }}>
            <label style={{ display: 'block', fontFamily: 'var(--font-body)', fontSize: '0.75rem', letterSpacing: '1px', textTransform: 'uppercase', color: 'var(--mid-gray)', marginBottom: 'var(--space-s)' }}>
              Тип
            </label>
            <select
              value={formData.item_type}
              onChange={(e) => setFormData({ ...formData, item_type: e.target.value })}
              style={{ width: '100%', padding: '12px', fontFamily: 'var(--font-body)', fontSize: '0.9375rem', border: '1px solid var(--soft-gray)', backgroundColor: 'white' }}
            >
              {ITEM_TYPES.map((type) => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>

          {/* Season */}
          <div style={{ marginBottom: 'var(--space-l)' }}>
            <label style={{ display: 'block', fontFamily: 'var(--font-body)', fontSize: '0.75rem', letterSpacing: '1px', textTransform: 'uppercase', color: 'var(--mid-gray)', marginBottom: 'var(--space-s)' }}>
              Сезон
            </label>
            <select
              value={formData.season}
              onChange={(e) => setFormData({ ...formData, season: e.target.value })}
              style={{ width: '100%', padding: '12px', fontFamily: 'var(--font-body)', fontSize: '0.9375rem', border: '1px solid var(--soft-gray)', backgroundColor: 'white' }}
            >
              {SEASONS.map((season) => (
                <option key={season.value} value={season.value}>{season.label}</option>
              ))}
            </select>
          </div>

          {/* Notes */}
          <div style={{ marginBottom: 'var(--space-l)' }}>
            <label style={{ display: 'block', fontFamily: 'var(--font-body)', fontSize: '0.75rem', letterSpacing: '1px', textTransform: 'uppercase', color: 'var(--mid-gray)', marginBottom: 'var(--space-s)' }}>
              Заметки
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={2}
              style={{ width: '100%', padding: '12px', fontFamily: 'var(--font-body)', fontSize: '0.9375rem', border: '1px solid var(--soft-gray)', boxSizing: 'border-box', resize: 'vertical' }}
            />
          </div>

          <button
            type="submit"
            disabled={loading || !formData.image_url || !formData.item_kind}
            style={{
              width: '100%',
              padding: '16px',
              backgroundColor: 'var(--black)',
              color: 'var(--white)',
              border: 'none',
              fontFamily: 'var(--font-body)',
              fontSize: '0.8125rem',
              letterSpacing: '1px',
              textTransform: 'uppercase',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.7 : 1
            }}
          >
            {loading ? 'Добавление...' : 'Добавить'}
          </button>
        </form>
        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </div>
    </div>
  );
};

export default AddItemModal;
